import { createSignal, onCleanup, Show, createMemo, createEffect } from "solid-js";
import AudioPlayer from "./AudioPlayer";
import { SessionState } from "../types/SessionState";
import { TabKey } from "../types/ui";

const MINIMUM_UTTERANCES = 3;
import RecordingConsentDialog from "./RecordingConsentDialog";
import RecordingStatus from "./RecordingStatus";
import { useRecording } from "../hooks/useRecording";
import toast from "solid-toast";
import { getSessionRecordings } from "../utils/blobStorage";
import { RoomHub } from "../Multidevice/RoomHub";
import QrInvite from "./QRCode";
import { getActiveProfile } from "../contexts/ProfileContext";

export default function InterviewBanner(props: {
  uploadedFile: File;
  recordingBlobUrl?: string | null;
  sourceLang: string;
  targetLang: string;
  onSource: (v: string) => void;
  onTarget: (v: string) => void;
  contactType: string;
  onContactType: (v: string) => void;
  respondent: string;
  onRespondent: (v: string) => void;
  interviewer: string;
  captureMode?: "mic" | "multi" | "file";
  speaker2Lang?: string;
  onStartPauseResume: () => void;
  onStop: () => void;
  // onFinalize?: () => void;

  session: SessionState;
  seconds: number;
  isReadOnly?: boolean;
  utteranceCount?: number;
  sessionStatus?: "draft" | "in_progress" | "completed"; // Session workflow status
  activeTab: () => TabKey;
  setSourceLang: (lang: string) => void;
  setTargetLang: (lang: string) => void;
  onStart: () => void;
  onNavigateBack?: () => void;
  showValidation?: boolean;
  sessionId: string;
  onRecordingCompleted?: (blobUrl: string) => void; // Callback when recording is uploaded
  // demoMode?: boolean;
  // onToggleDemo?: (next: boolean) => void;
  // scenarios?: {
  //   id: string;
  //   title: string;
  //   contactType?: string;
  //   difficulty?: string;
  // }[];
  // selectedScenarioId?: string;
  // onSelectScenario?: (id: string) => void;
  // Interpreter mode popout controls (UI only for now)
  interpreterMode?: boolean;
  onOpenInterviewerView?: () => void;
  onOpenRespondentView?: () => void;
}) {
  const [showConsentDialog, setShowConsentDialog] = createSignal(false);
  const [guestJoined, setGuestJoined] = createSignal(false);
  const [showWaitDialog, setShowWaitDialog] = createSignal(false);
  const recording = useRecording(props.sessionId);
  const [isFinalizing, setIsFinalizing] = createSignal(false);
  const [downloadUrl, setDownloadUrl] = createSignal<string | null>(null);
  // const [showDetails, setShowDetails] = createSignal(false); // Removed toggle functionality - session details should always be visible
  const [showQR, setShowQR] = createSignal(false);
  // Add function to get recording URL
  const fetchRecordingUrl = async () => {
    try {
      const recordings = await getSessionRecordings(props.sessionId);
      if (recordings.length > 0) {
        setDownloadUrl(recordings[0]);
      }
    } catch (error) {
      console.error("Failed to get recording URL:", error);
    }
  };

  // Fetch recording URL when session is completed
  createEffect(() => {
    if (props.sessionStatus === "completed") {
      fetchRecordingUrl();
    }
  });

  // Also fetch recording URL when local recording completes
  createEffect(() => {
    if (recording.status() === "completed") {
      fetchRecordingUrl();
    }
  });

  // Fetch recording URL when live recording completes and notify parent
  createEffect(() => {
    const status = recording.status();
    if (status === "completed" && props.captureMode !== "file") {
      console.log("[InterviewBanner] Live recording completed, fetching blob URL...");
      // Wait a bit for the upload to complete and database to update
      setTimeout(() => {
        fetchRecordingUrl().then(() => {
          // Notify parent that recording is ready
          if (downloadUrl() && props.onRecordingCompleted) {
            props.onRecordingCompleted(downloadUrl()!);
          }
        });
      }, 100);
    }
  });

  // Update recording state when transcription state changes
  createEffect(() => {
    const currentSession = props.session;
    if (currentSession !== SessionState.Idle) {
      recording.updateTranscriptionState(currentSession);
    }
  });

  console.log("props.blobUrl", props.recordingBlobUrl);


  // createEffect(() => {
  //   if (props.captureMode === "file" && props.session === SessionState.Idle && (props.utteranceCount || 0) > 0) {
  //     // GUARD: If we are already finalizing, don't start again
  //     if (isFinalizing()) return;
      
  //     setIsFinalizing(true);
  //     console.log("[Auto-Stop] Triggering finalization once...");
  //     props.onStop();
  //   }
  // });
  // Multi-device presence gating
  createEffect(() => {
    if (props.captureMode !== "multi") return;
    const hub = new RoomHub();
    let off: (() => void) | undefined;
    hub
      .connect()
      .then(() => hub.join(props.sessionId))
      .then(() => {
        off = hub.onGuestPresence((p) => {
          if (p.state === "joined") setGuestJoined(true);
          if (p.state === "mic_on") setGuestJoined(true);
          if (p.state === "mic_off") setGuestJoined(true);
        });
      })
      .catch(() => {});
    onCleanup(() => {
      try {
        off?.();
        hub.disconnect();
      } catch {}
    });
  });

  // Cleanup on unmount
  onCleanup(async () => {
    try {
      if (recording.isRecording() || recording.isPaused()) {
        await recording.stopRecording();
      }
    } catch (error) {
      console.error("Failed to stop recording during cleanup:", error);
      toast.error(
        "Failed to properly stop recording. Your recording might be incomplete.",
      );
    }
  });

  const isIdle = () => props.session === SessionState.Idle;
  const isConnected = () => props.session === SessionState.Connected;
  const isListening = () => props.session === SessionState.Listening;
  const canFinalize = () => false; // finalize disabled

  const getSessionStatusDisplay = () => {
    switch (props.sessionStatus) {
      case "completed":
        return { color: "bg-green-100 text-green-800", text: "Completed" };
      case "in_progress":
        return { color: "bg-blue-100 text-blue-800", text: "In Progress" };
      default:
        return { color: "bg-gray-100 text-gray-800", text: "Draft" };
    }
  };
  const PUBLIC_ORIGIN =
    import.meta.env.VITE_PUBLIC_ORIGIN || window.location.origin;

  const joinUrl = () => {
    const isInterp = !!props.interpreterMode;
    const guestLang = isInterp
      ? props.speaker2Lang || props.targetLang || "en-US" // different-language
      : props.sourceLang || "en-US"; // same-language multi-mic

    const path = "join";
    const base = `${PUBLIC_ORIGIN}/${path}/${encodeURIComponent(
      String(props.sessionId),
    )}`;
    const params = new URLSearchParams({ lang: String(guestLang) });
    try {
      const prof = getActiveProfile();
      if (prof) params.set("profile", prof);
    } catch {}
    return `${base}?${params.toString()}`;
  };

  const handleStart = async () => {
    try {
      // Gate multi-device start on respondent presence; show info dialog instead of starting
      if (
        props.captureMode === "multi" &&
        props.session === SessionState.Idle &&
        !guestJoined()
      ) {
        setShowWaitDialog(true);
        return;
      }
      if (isIdle()) {
        setShowConsentDialog(true);
      } else if (props.session === SessionState.Listening) {
        await recording.pauseRecording();
        props.onStartPauseResume();
      } else {
        await recording.resumeRecording();
        props.onStartPauseResume();
      }
    } catch (error) {
      console.error("Failed to handle recording state change:", error);
      toast.error("Failed to change recording state. Please try again.");
    }
  };

  const handleStop = async () => {
    try {
      // First complete the recording
      console.log("InterviewBanner.tsx handleStop - stopping recording");
      await recording.stopRecording();
      // Then stop the session
      props.onStop();
    } catch (error) {
      console.error("Failed to stop recording:", error);
      toast.error(
        "Failed to properly stop recording. Your recording might be incomplete.",
      );
    }
  };

  const handleConsentAccept = async () => {
    setShowConsentDialog(false);
    try {
      const success = await recording.startRecording();
      if (success) {
        props.onStart();
      } else {
        // Still allow session to start even if recording fails
        props.onStart();
        console.error("Failed to start recording");
      }
    } catch (error) {
      console.error("Failed to start recording:", error);
      toast.error("Failed to start recording. Please refresh and try again.");
      props.onStart(); // Still allow session to start
    }
  };

  const handleConsentDecline = () => {
    setShowConsentDialog(false);
    // Start session without recording
    props.onStart();
  };

  const btnText = () => {
    if (props.captureMode === "file") {
      if (props.session === SessionState.Idle) return "▶ Process File";
      return props.session === SessionState.Listening ? "⏸ Pause" : "▶ Resume";
    }
    if (props.session === SessionState.Idle) return "▶ Start";
    return props.session === SessionState.Listening ? "⏸ Pause" : "▶ Resume";
  };

  const hasOutputs = () => props.sessionStatus === "completed";

  return (
    <div class="banner-card mx-auto relative px-0">
      <div class="grid grid-cols-12 items-center px-1 h-14 gap-2 font-medium">
        {/* Left: Session summary */}
        <div
          class={`min-w-0 flex items-center justify-center gap-4 h-14 px-4 bg-gray-100 ${
            hasOutputs() ? "col-span-7" : "col-span-9"
          }`}
        >
          {/* Session Details - Always visible for better UX */}
          <div class="flex flex-nowrap items-baseline gap-6 min-w-0 overflow-hidden">
            <span class="text-sm inline-flex items-baseline gap-1 whitespace-nowrap">
              <strong>Contact:</strong>
              <span>{props.contactType}</span>
            </span>
            <span class="text-sm inline-flex items-baseline gap-1 whitespace-nowrap">
              <strong>Interviewer:</strong>
              <span>{props.interviewer}</span>
            </span>
            <span class="text-sm inline-flex items-baseline gap-1 whitespace-nowrap">
              <strong>Respondent:</strong>
              <span>{props.respondent}</span>
            </span>
            <span class="text-sm inline-flex items-baseline gap-1 whitespace-nowrap">
              <strong>Lang:</strong>
              <span>
                {props.sourceLang}→{props.targetLang || "None"}
              </span>
            </span>
          </div>
        </div>

        {/* Middle: Controls */}
        <div
          class={`flex items-center justify-center gap-2 h-14 px-4 bg-gray-100 min-w-0 overflow-x-auto ${
            hasOutputs() ? "col-span-3" : "col-span-3"
          }`}
        >
          {(() => {
            const s = getSessionStatusDisplay();
            return (
              <span
                class={`px-2 py-[2px] rounded text-xs font-medium ${s.color}`}
                title="Session status"
              >
                {s.text}
              </span>
            );
          })()}
          <Show when={!isIdle() && props.captureMode !== "file"}>
            <RecordingStatus
              status={recording.status() as any}
              duration={recording.duration()}
            />

           
          </Show>
          {/* Finalize button removed */}

          {/* Show AudioPlayer for:
               1. File upload sessions (during processing or after completion)
               2. Live recording sessions after the recording is completed and uploaded
          */}
          <Show when={
            (props.captureMode === "file" && props.uploadedFile) || 
            (props.recordingBlobUrl !== null && props.recordingBlobUrl !== undefined)
          }>
            <AudioPlayer 
              uploadedFile={props.uploadedFile}
              blobUrl={props.recordingBlobUrl || undefined}
              title="Recording"
            />
          </Show>
          
          <Show when={ !((props.captureMode === "file" && props.uploadedFile) || 
            (props.recordingBlobUrl !== null && props.recordingBlobUrl !== undefined))}>
            <button
              onClick={handleStart}
              disabled={props.sessionStatus === "completed"}
              class={`text-sm font-medium focus:outline-none whitespace-nowrap ${
                props.sessionStatus === "completed"
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-green-500 hover:text-green-500"
              }`}
              title={
                props.sessionStatus === "completed"
                  ? "Session is completed"
                  : undefined
              }
            >
              {btnText()}
            </button>
          </Show>
          <Show when={!isIdle()}>
            <button
              onClick={handleStop}
              class="text-sm font-medium text-red-500 hover:text-red-800 focus:outline-none whitespace-nowrap"
            >
              ■ Stop
            </button>
          </Show>
          <Show when={props.captureMode === "multi"}>
            <button
              type="button"
              class="text-sm font-medium text-blue-600 hover:text-blue-800 focus:outline-none whitespace-nowrap"
              onClick={() => setShowQR(true)}
              disabled={props.sessionStatus === "completed"}
              classList={{
                "opacity-50": props.sessionStatus === "completed",
                "cursor-not-allowed": props.sessionStatus === "completed",
              }}
              title={
                props.sessionStatus === "completed"
                  ? "Session is completed"
                  : "Invite respondent by QR / link"
              }
            >
              📱 Invite
            </button>
          </Show>
          <Show when={showQR()}>
            <QrInvite url={joinUrl()} onClose={() => setShowQR(false)} />
          </Show>
          {/* recording download moved to Outputs segment */}
          {/** Demo UI disabled **/}
          {false && <div />}
          {/* Popout buttons - align with existing controls */}
          <Show
            when={
              props.interpreterMode &&
              (props.captureMode === "mic" || props.captureMode === "multi")
            }
          >
            <div class="flex items-center gap-1 flex-shrink-0">
              <button
                type="button"
                class="text-[11px] px-2 py-[4px] rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 whitespace-nowrap"
                title="Open Interviewer view"
                disabled={props.sessionStatus === "completed"}
                classList={{
                  "opacity-50 cursor-not-allowed":
                    props.sessionStatus === "completed",
                }}
                onClick={() => props.onOpenInterviewerView?.()}
              >
                ↗ Interviewer
              </button>
              <Show when={props.captureMode === "mic"}>
                <button
                  type="button"
                  class="text-[11px] px-2 py-[4px] rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 whitespace-nowrap"
                  title="Open Respondent view"
                  disabled={props.sessionStatus === "completed"}
                  classList={{
                    "opacity-50 cursor-not-allowed":
                      props.sessionStatus === "completed",
                  }}
                  onClick={() => props.onOpenRespondentView?.()}
                >
                  ↗ Respondent
                </button>
              </Show>
            </div>
          </Show>
        </div>
        {/* Right: Outputs (Open Report + Recording) */}
        <Show when={hasOutputs()}>
          <div class="col-span-2 flex items-center justify-center gap-3 h-14 px-4 bg-gray-100 min-w-0 overflow-x-auto">
            <button
              id="open-report-btn"
              class="text-sm font-medium text-blue-600 hover:text-blue-800 focus:outline-none whitespace-nowrap disabled:text-gray-400"
              disabled={props.sessionStatus !== "completed"}
              onClick={() => {
                const ev = new CustomEvent("open-report", {
                  detail: {
                    sessionId: props.sessionId,
                    contactType: props.contactType,
                  },
                });
                window.dispatchEvent(ev);
              }}
            >
              Open Report
            </button>
            <Show when={downloadUrl()}>
              <a
                href={downloadUrl()!}
                target="_blank"
                rel="noopener"
                class="text-blue-600 hover:text-blue-800 text-sm underline whitespace-nowrap"
                title="Download recording"
              >
                ⬇ Recording
              </a>
            </Show>
          </div>
        </Show>
      </div>
      {/* ─── Consent Dialog (unchanged) ──────────────────── */}
      <RecordingConsentDialog
        isOpen={showConsentDialog()}
        onAccept={handleConsentAccept}
        onDecline={handleConsentDecline}
      />{" "}
      <Show when={showWaitDialog()}>
        <div class="fixed inset-0 bg-black/30 z-[9998] flex items-center justify-center p-4">
          <div class="bg-white rounded-md shadow-lg p-4 w-[360px] max-w-[90vw]">
            <div class="text-sm mb-3">
              The respondent hasn’t joined yet. Please share the QR/link and
              wait for them to connect.
            </div>
            <div class="flex items-center justify-end gap-2">
              <button
                class="px-3 py-1 border rounded"
                onClick={() => setShowWaitDialog(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      </Show>
      {/* Popout buttons (UI only) are rendered inline in the right control bar above */}
    </div>
  );
}
